import { useCallback, useEffect, useRef, useState } from 'react';

import { isAuthenticated, useAuth } from '@suntex.fim/auth/react';
import { FavoriteGamesAction, MetaKey } from '@suntex.fim/enums';
import { useFramework } from '@suntex.fim/framework';

export interface FavoriteModules {
  slugs: string[];
  lastUpdated: number;
}

const EMPTY_FAVORITES: FavoriteModules = { slugs: [], lastUpdated: 0 };

function getFavoriteModuleKey(slug: string, variant?: number): string {
  return variant === undefined || variant === 0 ? slug : `${slug}|${variant}`;
}

export function useFavoriteModules() {
  const { api } = useFramework();
  const { login } = useAuth(isAuthenticated);
  const loginId = login.id;

  const [favorites, setFavorites] = useState<FavoriteModules>(EMPTY_FAVORITES);
  const favoritesRef = useRef<FavoriteModules>(EMPTY_FAVORITES);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    favoritesRef.current = favorites;
  }, [favorites]);

  const fetchFavorites = useCallback(async () => {
    if (!loginId) return;
    setIsLoading(true);
    try {
      const result = await api.get_Login_LoginMeta({
        loginId,
        metaKey: MetaKey.FavoriteModules,
      });
      const data = (result.data as Partial<FavoriteModules> | undefined) ?? {};
      setFavorites({
        slugs: data.slugs ?? [],
        lastUpdated: data.lastUpdated ?? Date.now(),
      });
    } catch {
      // silently retain existing state on fetch error
    } finally {
      setIsLoading(false);
    }
  }, [api, loginId]);

  useEffect(() => {
    void fetchFavorites();
  }, [fetchFavorites]);

  const toggleFavorite = useCallback(
    async (
      slug: string,
      variant: number | undefined,
      action: FavoriteGamesAction.Add | FavoriteGamesAction.Remove,
    ) => {
      if (!loginId) return;

      const prev = favoritesRef.current;
      const favoriteKey = getFavoriteModuleKey(slug, variant);
      const alreadyPresent = prev.slugs.includes(favoriteKey);

      if (action === FavoriteGamesAction.Add && alreadyPresent) return;
      if (action === FavoriteGamesAction.Remove && !alreadyPresent) return;

      const updatedSlugs =
        action === FavoriteGamesAction.Remove
          ? prev.slugs.filter((storedSlug) => storedSlug !== favoriteKey)
          : [...prev.slugs, favoriteKey];
      const next: FavoriteModules = {
        slugs: updatedSlugs,
        lastUpdated: Date.now(),
      };

      setFavorites(next);
      setIsLoading(true);
      try {
        await api.post_Login_UpsertLoginMeta({
          loginId,
          metaKey: MetaKey.FavoriteModules,
          data: next,
        });
      } catch {
        // Roll back on error
        setFavorites(prev);
      } finally {
        setIsLoading(false);
      }
    },
    [api, loginId],
  );

  const isFavorite = useCallback(
    (slug: string, variant: number | undefined) =>
      favorites.slugs.includes(getFavoriteModuleKey(slug, variant)),
    [favorites],
  );

  return { favorites, isFavorite, toggleFavorite, isLoading };
}
